import type {
  defaultFeatureValues,
  FeatureFlag,
} from '@/hooks/useFeatureFlag/defaults';
import type { LaunchOption } from '@/hooks/useLauncher';
import { ExperienceTypes, OrganizationType } from '@/types/gql.generated';
import type { ReturnToModal } from '../hooks/useUpgradeModal';
import type { PricingTierName } from '../types';

/** Every capability the app puts behind a plan. */
export type PaywallFeature =
  | 'customSlug'
  | 'customColors'
  | 'premiumVibe'
  | 'premiumPalette'
  | 'customPalette'
  | 'addView'
  | 'externalSource'
  | 'collaborators'
  | 'textMessages'
  | 'scheduleTextMessages'
  | 'magicChatFollowup'
  | 'magicFileJob';

/**
 * Which funnel event a gate reports. Schedules and experiences are separate
 * surfaces with separate funnels, so the event has to follow the gate rather than
 * being fixed to one of them.
 */
export type GateAnalyticsEvent =
  'experience:click upgrade' | 'entry:click upgrade';

/**
 * Which tier answers for the feature.
 *
 * `user` — the signed-in user's own entitlement. A free owner can invite a Plus
 * editor, and that editor may then use the feature on the owner's experience.
 * Right for content and styling choices that only affect what they're editing.
 *
 * `experience` — the entitlement of the org the experience is homed in. Right for
 * anything the owner has to be able to maintain afterwards: an editor shouldn't
 * turn on a capability the owner can't keep.
 */
export type GateScope = 'user' | 'experience';

/**
 * `plus` is the only requirement UserTier can express (it is just FREE/PRO), so
 * `family` and `business` additionally read the org's type and funding plan.
 */
export type GateRequirement = 'plus' | 'family' | 'business';

/** Feature flags whose default is a number, so they can hold an allowance. */
export type NumericFeatureFlag = {
  [K in FeatureFlag]: (typeof defaultFeatureValues)[K] extends number
    ? K
    : never;
}[FeatureFlag];

/** Feature flags whose default is a boolean, so they can act as a switch. */
export type BooleanFeatureFlag = {
  [K in FeatureFlag]: (typeof defaultFeatureValues)[K] extends boolean
    ? K
    : never;
}[FeatureFlag];

export type GateRule = {
  scope: GateScope;
  requires: GateRequirement;
  /**
   * Contextual title for the compare-plans upgrade modal, naming the capability the
   * gate is selling rather than the generic "Compare Plans" / "Upgrade to {Tier}"
   * derived from the columns. Lives on the rule so the header follows the feature
   * that opened the gate.
   */
  heading?: string;
  /**
   * Pricing columns the upgrade modal pitches. Defaults to the columns implied by
   * `requires`; set it only when a gate wants to sell something narrower or wider.
   */
  tiers?: PricingTierName[];
  /** Post-checkout resume key, also reported as the analytics `launch` value. */
  launch?: LaunchOption;
  /**
   * Modal to reopen when the upgrade modal is dismissed. Lives here rather than
   * at the call site so a shared CTA can't send the user back to a modal they
   * never came from.
   */
  returnTo?: ReturnToModal;
  /**
   * Numeric flag holding the allowance for users who don't meet `requires`. With
   * this set the gate only closes once the caller's `count` reaches the limit,
   * which is how "you may add 2, the 3rd needs Plus" is expressed. Takes
   * precedence over `freeLimit`.
   */
  limitFlag?: NumericFeatureFlag;
  /**
   * Same as `limitFlag` but fixed, for an allowance nobody needs to tune at
   * runtime.
   */
  freeLimit?: number;
  /**
   * Allowance once `requires` is met. Only meaningful alongside a free allowance;
   * omit for unlimited.
   */
  entitledLimit?: number;
  /**
   * Org types the feature is withheld from entirely — no lock and no upsell,
   * because it will never apply to them.
   */
  hiddenForOrgTypes?: OrganizationType[];
  /**
   * A rollout switch that opens the feature to every tier while it's on, used to
   * trial a paid capability before charging for it. Independent of `requires`:
   * either one being satisfied is enough.
   */
  overrideFlag?: BooleanFeatureFlag;
  /** Funnel event to report. Defaults to `experience:click upgrade`. */
  analyticsEvent?: GateAnalyticsEvent;
};

/**
 * A feature's rule, optionally overridden per experience type. Page experiences
 * and planners deliberately diverge: the same capability can carry a different
 * scope, requirement, or allowance depending on what it is being used on.
 * `null` means the feature is not gated in that context.
 */
export type GateDefinition = {
  default: GateRule | null;
  byExperienceType?: Partial<Record<ExperienceTypes, GateRule | null>>;
};

// Page experiences keep the allowance they have always had; only the planner's
// tighter limit is worth tuning at runtime.
const PAGE_FREE_MAX_COLLABORATORS = 5;
const PRO_MAX_COLLABORATORS = 250;

export const PAYWALL_GATES: Record<PaywallFeature, GateDefinition> = {
  // Experience-scoped: the address outlives whoever typed it, so it follows the org
  // the experience is homed in rather than the entitlement of whoever happens to be
  // editing. Pairs with the owner-only rule in CopyLinkSection — the owner decides,
  // and the owner's workspace is what pays for it.
  //
  // Not `ownerOnly` even though only the owner may set one: collaborators still need
  // to see and copy the URL, and `ownerOnly` reports `isHidden`, which would both
  // render nothing and drop `isLocked`. CopyLinkSection holds the owner check so it
  // can keep showing a read-only link.
  customSlug: {
    default: {
      scope: 'experience',
      requires: 'plus',
      heading: 'Customize Your Link with Plus',
      launch: 'share-modal',
      returnTo: 'share',
    },
  },

  customColors: {
    default: {
      scope: 'user',
      requires: 'plus',
      heading: 'Unlock Custom Colors with Plus',
      launch: 'controls:styles',
      overrideFlag: 'experiencesCustomColorEnabled',
    },
  },

  premiumVibe: {
    default: {
      scope: 'user',
      requires: 'plus',
      heading: 'Unlock Premium Vibes with Plus',
    },
  },

  // The built-in palettes flagged requiresPaidPlan. Its own entry rather than
  // sharing customPalette below: that one sells the not-yet-built palette
  // builder, and folding the two together would make an upgrade from the preset
  // list indistinguishable from one for authoring your own. User-scoped like
  // premiumVibe — a styling choice that only affects what the editor is editing.
  premiumPalette: {
    default: {
      scope: 'user',
      requires: 'plus',
      heading: 'Unlock Premium Palettes with Plus',
    },
  },

  customPalette: {
    default: {
      scope: 'user',
      requires: 'plus',
      heading: 'Create Custom Palettes with Plus',
    },
  },

  // Views stay visible to everyone; only adding past the free allowance is gated.
  addView: {
    default: null,
    byExperienceType: {
      [ExperienceTypes.Planner]: {
        scope: 'user',
        requires: 'plus',
        heading: 'Add More Views with Plus',
        limitFlag: 'freeTierViewLimit',
      },
    },
  },

  // Pulling in another Agenda Hero experience stays free and doesn't count; the
  // allowance covers external calendars (Google, Outlook, ICS) only, and connecting
  // one past it is what needs Plus. Counted per experience, like `addView`. A flag
  // value of 0 restores the all-or-nothing gate this replaced, since the count then
  // starts at the allowance.
  externalSource: {
    default: {
      scope: 'user',
      requires: 'plus',
      heading: 'Connect External Calendars with Plus',
      limitFlag: 'freeTierExternalSourceLimit',
    },
  },

  // Seats follow the org the experience is homed in, on both types: the owner is the
  // one left maintaining those collaborators, so an editor's own Plus shouldn't buy
  // seats onto someone else's free page. Planners allow fewer on free, which is the
  // only thing that differs between the two rules.
  //
  // `default` is what a *schedule* resolves — it consumes useCollaboratorLimit with
  // no experience in context. It has to stay user-scoped: a schedule has no home org,
  // so reading one would resolve every Plus user to the free allowance.
  collaborators: {
    default: {
      scope: 'user',
      requires: 'plus',
      heading: 'Add More Collaborators with Plus',
      freeLimit: PAGE_FREE_MAX_COLLABORATORS,
      entitledLimit: PRO_MAX_COLLABORATORS,
      launch: 'share-modal',
      returnTo: 'share',
    },
    byExperienceType: {
      [ExperienceTypes.Page]: {
        scope: 'experience',
        requires: 'plus',
        heading: 'Add More Collaborators with Plus',
        freeLimit: PAGE_FREE_MAX_COLLABORATORS,
        entitledLimit: PRO_MAX_COLLABORATORS,
        launch: 'share-modal',
        returnTo: 'share',
      },
      [ExperienceTypes.Planner]: {
        scope: 'experience',
        requires: 'plus',
        heading: 'Add More Collaborators with Plus',
        limitFlag: 'freeTierCollaboratorLimit',
        entitledLimit: PRO_MAX_COLLABORATORS,
        launch: 'share-modal',
        returnTo: 'share',
      },
    },
  },

  // A family-plan capability. Business workspaces will never get it, so it is
  // hidden there rather than teased.
  textMessages: {
    default: {
      scope: 'experience',
      requires: 'family',
      heading: 'Text Reminders with a Family Plan',
      hiddenForOrgTypes: [OrganizationType.Business],
    },
  },

  // Scheduled messages on a *schedule*, which is not an experience: user-scoped
  // Plus, and already enforced server-side by
  // @isAuthenticatedWithRole(role: EDITOR, tier: PRO). Registered so the schedule
  // pages share the one CTA; the decision is unchanged from what they did inline.
  scheduleTextMessages: {
    default: {
      scope: 'user',
      requires: 'plus',
      heading: 'Schedule Text Reminders with Plus',
      analyticsEvent: 'entry:click upgrade',
    },
  },

  // The server owns this decision (chat credits + the upgradePlan tool call).
  // Registered so the CTA gets consistent copy, launch key and analytics.
  magicChatFollowup: {
    default: {
      scope: 'user',
      requires: 'plus',
      heading: 'Keep the Magic Going with Plus',
      launch: 'magic-chat-continue',
    },
  },

  magicFileJob: {
    default: {
      scope: 'user',
      requires: 'plus',
      heading: 'Add Images & PDFs with Plus',
      launch: 'magic-file-upload',
    },
  },
};

/**
 * Which plans to pitch per requirement. `plus` gates pitch Organizations alongside Plus
 * and Family: a Business plan makes its buyer PRO — personally and for the orgs they own —
 * so it genuinely unlocks every `plus` gate, and a gate is the main place anyone learns the
 * plan exists. `family` stays alone because a Business plan does not satisfy it; text
 * reminders are Family-only, so pitching Organizations there would sell a plan that can't
 * deliver the feature. Free is never pitched: the user is either already on it, or past it.
 */
const PITCH_TIERS: Record<GateRequirement, PricingTierName[]> = {
  plus: ['Plus', 'Family', 'Organizations'],
  family: ['Family'],
  business: ['Organizations'],
};

export const resolveGateTiers = (rule: GateRule): PricingTierName[] =>
  rule.tiers ?? PITCH_TIERS[rule.requires];

export const resolveGateRule = (
  feature: PaywallFeature,
  experienceType: ExperienceTypes | undefined
): GateRule | null => {
  const { default: fallback, byExperienceType } = PAYWALL_GATES[feature];

  if (
    experienceType &&
    byExperienceType &&
    experienceType in byExperienceType
  ) {
    return byExperienceType[experienceType] ?? null;
  }

  return fallback;
};
